import React, { useState } from 'react';
import { ActivityIndicator, ScrollView, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { ThemedText } from '@/components/themed-text';
import { ErrorState } from '@/components/ui/error-state';
import { PressableScale } from '@/components/ui/pressable-scale';
import {
  useActivityCategories,
  useActivityPacks,
  useAdoptPack,
} from '@/hooks/use-onboarding-interview';
import { useToastStore } from '@/store/toastStore';
import { ActivityCategory } from '@/types/activity-pack';

const PRIMARY = '#FD5001';

interface Props {
  /** O pack curado foi adotado: segue para o resumo, como na conversa. */
  onAdopted: () => void;
  /** "Prefiro configurar sozinho". */
  onSkip: () => void;
  /** Texto do topo: muda se o agente caiu no meio da conversa. */
  interrupted?: boolean;
}

/**
 * O caminho sem IA (T-24): escolher o ramo numa lista e deixar o pack curado
 * preencher tudo.
 *
 * **Cadastro novo nunca fica bloqueado por indisponibilidade de IA** — sem
 * chave, com a API fora ou com a conversa travada, o prestador sai daqui com
 * serviços, agenda e contratos do ramo dele. Depois cai no mesmo resumo da
 * entrevista: o `apply()` continua sendo a única escrita.
 */
export function PackPicker({ onAdopted, onSkip, interrupted }: Props) {
  const [category, setCategory] = useState<ActivityCategory | null>(null);
  const categories = useActivityCategories();
  const packs = useActivityPacks(category?.id, !!category);
  const adopt = useAdoptPack();
  const { addToast } = useToastStore();

  const handleAdopt = async (slug: string) => {
    try {
      await adopt.mutateAsync(slug);
      onAdopted();
    } catch {
      addToast('Não consegui carregar esse ramo. Tente novamente.', 'error');
    }
  };

  if (categories.isLoading) {
    return (
      <View className="flex-1 items-center justify-center">
        <ActivityIndicator color={PRIMARY} />
      </View>
    );
  }

  if (categories.isError) {
    return (
      <ErrorState
        title="Não consegui carregar os ramos"
        message="Verifique sua conexão e tente novamente."
        onRetry={() => void categories.refetch()}
      />
    );
  }

  return (
    <View className="flex-1">
      <ScrollView
        keyboardShouldPersistTaps="handled"
        contentContainerClassName="px-6 py-6 gap-4"
        showsVerticalScrollIndicator={false}
      >
        <View className="gap-1">
          <ThemedText className="font-bold text-2xl text-foreground">
            {category ? category.label : 'O que você faz?'}
          </ThemedText>
          <ThemedText className="text-sm leading-5 text-foreground/55">
            {category
              ? 'Escolha o que mais se parece com o seu trabalho. Depois dá para mudar tudo.'
              : interrupted
                ? 'Vamos por aqui: escolha o seu ramo e eu configuro o resto.'
                : 'Escolha o seu ramo e eu já deixo seus serviços e sua agenda prontos.'}
          </ThemedText>
        </View>

        {category ? (
          <>
            <PressableScale
              onPress={() => setCategory(null)}
              className="flex-row items-center gap-2 self-start rounded-full px-1 py-1"
            >
              <Ionicons className="text-neutral-400" name="chevron-back" size={14} />
              <ThemedText className="font-semibold text-xs text-foreground/50">
                Ver todos os ramos
              </ThemedText>
            </PressableScale>

            {packs.isLoading ? (
              <ActivityIndicator color={PRIMARY} />
            ) : (
              (packs.data ?? []).map((pack) => (
                <PickerCard
                  key={pack.slug}
                  icon="briefcase-outline"
                  title={pack.label}
                  subtitle={`${pack.serviceCount} ${
                    pack.serviceCount === 1 ? 'serviço já configurado' : 'serviços já configurados'
                  }`}
                  disabled={adopt.isPending}
                  onPress={() => void handleAdopt(pack.slug)}
                />
              ))
            )}
          </>
        ) : (
          (categories.data ?? []).map((item) => (
            <PickerCard
              key={item.id}
              icon={(item.icon as keyof typeof Ionicons.glyphMap) ?? 'grid-outline'}
              title={item.label}
              subtitle={`${item.packCount} ${item.packCount === 1 ? 'ramo' : 'ramos'}`}
              onPress={() => setCategory(item)}
            />
          ))
        )}
      </ScrollView>

      <View className="px-6 pb-4 pt-2">
        <PressableScale onPress={onSkip} className="items-center py-2">
          <ThemedText className="font-semibold text-xs text-foreground/45">
            Prefiro configurar sozinho
          </ThemedText>
        </PressableScale>
      </View>
    </View>
  );
}

function PickerCard({
  icon,
  title,
  subtitle,
  onPress,
  disabled,
}: {
  icon: keyof typeof Ionicons.glyphMap;
  title: string;
  subtitle: string;
  onPress: () => void;
  disabled?: boolean;
}) {
  return (
    <PressableScale
      onPress={onPress}
      disabled={disabled}
      className="flex-row items-center gap-3 rounded-2xl border border-border/40 bg-card p-4"
    >
      <View className="h-11 w-11 items-center justify-center rounded-full bg-primary/10 dark:bg-primary">
        <Ionicons className="text-primary dark:text-white" name={icon} size={20} />
      </View>
      <View className="flex-1 gap-0.5">
        <ThemedText className="font-semibold text-base text-foreground">{title}</ThemedText>
        <ThemedText className="text-xs text-foreground/50">{subtitle}</ThemedText>
      </View>
      <Ionicons className="text-neutral-400" name="chevron-forward" size={18} />
    </PressableScale>
  );
}
